import { wallOpeningPlanExtents, wallOpeningOpening3 } from "building-common/openings/openingQuery";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type {
  FoundationAssembly,
  IsolatedFoundationPickSource,
} from "building-types/foundations/foundationAssemblyEntityTypes";
import { isSolidFoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import type {
  FoundationComponentEntity,
  FootingEntity,
  PierEntity,
  CircularIsolatedFootingEntity,
  RectangularIsolatedFootingEntity,
  CircularCMUPierEntity,
  CircularCIPPierEntity,
  RectangularCMUPierEntity,
  RectangularCIPPierEntity,
} from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import type { FoundationCondition } from "building-types/foundations/foundationEnums";
import { FootingType, PierType } from "building-types/foundations/foundationEnums";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type { FoundationPathDatums } from "building-types/foundations/foundationPathTypes";
import type {
  FoundationExtrusionSize,
  FoundationPath,
  FoundationSection,
  FoundationSectionOpeningDescriptor,
  FoundationSectionSize,
  FoundationStrip,
  IsolatedFoundationSection,
  IsolatedFoundationSectionSize,
  LinearFoundationSection,
  LinearFoundationSectionSize,
  IntermediateFoundationSection,
  IntermediateIsolatedFoundationSection,
  IntermediateLinearFoundationSection,
  IntermediateFoundationExtrusionDescriptor,
  IntermediateExtrusionFrameInfo,
} from "building-types/foundations/foundationTypes";
import { FoundationSectionType } from "building-types/foundations/foundationTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type { WallEndCap } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallGap } from "building-types/walls/wallGap";
import type { WallPathBase } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew } from "common/utils/nominalNumbers";
import uid from "common/utils/uid";
import { startSortedWallGapsNew } from "doc/utils/wallGapTools";
import { arc2ByCenterStartAngle } from "geom/arc";
import { faceNew, faceSimplifyLoops } from "geom/brep";
import { PolyLine2PolyLine2ContainmentClass, classifyContainmentPolyLine2PolyLine2 } from "geom/containment";
import { ANGLE_EPSILON, COMPACTNESS_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFace } from "geom/extrude";
import { line2Eval, line2Normal, line2Tangent } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { planeNew, planeNewXY } from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2Close,
  polyLine2Copy,
  polyLine2Move,
  polyLine2Rotate,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AABB3 } from "geom/polyLine3";
import { vec2DistSquared, vec2Dot, vec2Rotate } from "geom/vec2";
import { getDoorRoughOpeningAdjustment, isGarageDoorOperationType } from "../doors/doorUtils";
import { floorPathBottom, floorPathOpposite, floorPathTop } from "../floor/floorPathUtils";
import {
  makeCrossSectionFromWallGaps,
  makeWallGap,
  makeWallGapCrossSections,
} from "../walls/solidWallCrossSectionUtils";
import { pierParamPositionOnEdge } from "./foundationPattern";
import { foundationPathCoordinates } from "./pathCoordinates";
import { slabDepthAtFoundationPath } from "./slabUtils";

export function foundationStripNew({
  path,
  sections = [],
}: {
  path: FoundationPath;
  sections?: FoundationSection[];
}): FoundationStrip {
  return {
    id: uid(),
    path,
    sections,
  };
}
export function foundationPathNew(
  halfEdgeIndex: number,
  planarGraphId: string,
  condition: FoundationCondition,
  assembly: FoundationAssembly,
  datums: FoundationPathDatums,
  isolatedAssemblies: FoundationAssembly[] = [],
  subSystemOffset: number,
  superSystemOffset: number,
): FoundationPath {
  return {
    halfEdgeIndex,
    planarGraphId,
    condition,
    assembly,
    isolatedAssemblies,
    datums,
    subSystemOffset,
    superSystemOffset,
  };
}

export function resolveFoundationComponentTopBottom({
  foundationPath,
  level,
  defaultTop,
  defaultBottom,
}: {
  foundationPath: Pick<FoundationPath, "isDerivedFromHorizontalAssembly" | "assembly" | "datums">;
  level: Level;
  defaultTop: number;
  defaultBottom: number;
}): { top: number; bottom: number; depth: number } {
  let top = defaultTop;
  let bottom = defaultBottom;

  /**
   * @note if the foundation path is derived from a horizontal assembly, we need to use the foundation assembly datum abstractions
   * to get the top and bottom of the foundation component. These overrides must be set on the foundation assembly else it
   * will fall back to the legacy behavior.
   * @note instance offset is being when foundation path is derived from horizontal assemblies. TBD and expanded later.
   */
  if (foundationPath.isDerivedFromHorizontalAssembly && foundationPath.assembly) {
    const topDatumAbstraction = foundationPath.assembly.topDatumAbstraction;
    const bottomDatumAbstraction = foundationPath.assembly.bottomDatumAbstraction;

    if (topDatumAbstraction) {
      top = topDatumAbstraction.resolveVerticalDatum({
        level,
        path: foundationPath,
      });
    }
    if (bottomDatumAbstraction) {
      bottom = bottomDatumAbstraction.resolveVerticalDatum({
        level,
        path: foundationPath,
      });
    }
  } else {
    const { instanceOffset } = level.levelDatumsGenerated;
    bottom += instanceOffset;
  }

  const depth = Math.abs(top - bottom);

  return { top, bottom, depth };
}

/**
 * Groups foundation paths and floor paths that emanate from a vertex.
 * Used to simplify function signatures that need both foundation and floor paths together.
 */
export interface FoundationPathVertexPaths {
  readonly foundationPaths: FoundationPath[];
  readonly floorPaths: FloorPath[];
}

/**
 * Groups foundation paths and floor paths for both start and end vertices of a foundation path.
 * Used to simplify function signatures that need both start and end vertex paths.
 */
export interface FoundationPathEnds {
  readonly fromStart: FoundationPathVertexPaths;
  readonly fromEnd: FoundationPathVertexPaths;
}

function isolatedFoundationAssembliesOpposite(
  foundationPath: FoundationPath,
  planarGraphs: Table<PolygonPlanarGraph>,
): FoundationAssembly[] {
  const { isolatedAssemblies } = foundationPath;
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);

  return isolatedAssemblies.map((assembly) => {
    if (!isSolidFoundationAssembly(assembly)) {
      return assembly;
    }
    const { pierPositions, pierOffset = [] } = assembly;
    const newPierPositions = pierPositions?.map((position) => {
      const [x, y] = line2Eval(startX, startY, endX, endY, position.param, position.paramNormalized);
      return { ...position, ...pierParamPositionOnEdge(endX, endY, startX, startY, x, y) };
    });
    return { ...assembly, pierPositions: newPierPositions, pierOffset: [-pierOffset[0], -pierOffset[1]] };
  });
}

export function foundationPathOpposite(
  foundationPath: FoundationPath,
  planarGraphs: Table<PolygonPlanarGraph>,
): FoundationPath {
  const { condition, assembly, subSystemOffset, superSystemOffset, datums, planarGraphId, halfEdgeIndex } =
    foundationPath;

  return {
    halfEdgeIndex: planarGraphAdjacentHalfEdge(halfEdgeIndex),
    planarGraphId,
    condition,
    assembly,
    isolatedAssemblies: isolatedFoundationAssembliesOpposite(foundationPath, planarGraphs),
    subSystemOffset: -subSystemOffset,
    superSystemOffset: -superSystemOffset,
    datums,
  };
}

/**
 * foundationPathFloorPerimeterEndCapOffset detects how far sections on this foundation path should extend on
 * the half edge in the event that a wall end cap needs to be placed. This is done by
 * determining the closest non-collinear offet at that vertex, or 0 in the event that
 * all the edges are collinear
 */
function foundationPathFloorPerimeterEndCapOffset({
  foundationPath,
  floorPath,
  foundationPathsFromVertex,
  floorPathsFromVertex,
  planarGraphs,
}: {
  foundationPath: FoundationPath; // this needs to be oriented to match the pathsFromVertex
  floorPath?: FloorPath;
  foundationPathsFromVertex: FoundationPath[];
  floorPathsFromVertex: FloorPath[];
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  const { subSystemOffset, condition, halfEdgeIndex } = foundationPath;
  const { leftRoomClass, rightRoomClass, floorPriority } = floorPath ?? {
    leftRoomClass: RoomClass.EXTERIOR,
    rightRoomClass: RoomClass.EXTERIOR,
    floorPriority: PathPriority.NONE,
  };
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
  const oClass = floorPriority === PathPriority.LEFT ? leftRoomClass : rightRoomClass;
  const oOffset = floorPriority === PathPriority.LEFT ? subSystemOffset : -subSystemOffset;
  const oIndex = halfEdgeIndex;
  const oAdj = planarGraphAdjacentHalfEdge(oIndex);
  const [tx, ty] = line2Tangent(startX, startY, endX, endY);

  let vClass = oClass;
  let vCondition = condition;
  let vIndex = oIndex;
  let vOffset: number | undefined;

  // should this end cap project to the furthest offset (this will be a FoundationPattern setting)
  // default is to use the closest edge to minimize overlaps
  const offsetProjection = false;

  /**
   * We are iterating the planar graph edge foundation paths emanating from a vertex,
   * looking for any that are not collinear with the test path
   */
  for (let i = 0; i < foundationPathsFromVertex.length; i++) {
    const vPath = foundationPathsFromVertex[i];
    if (!vPath) {
      continue;
    }
    const vFloorPath = floorPathsFromVertex.find(
      (path) => path.halfEdgeIndex === vPath.halfEdgeIndex && path.planarGraphId === vPath.planarGraphId,
    );
    if (!vFloorPath) {
      continue;
    }
    const { subSystemOffset: sOffset } = vPath;
    const { leftRoomClass: lClass, rightRoomClass: rClass, floorPriority: vPriority } = vFloorPath;
    const [vStartX, vStartY, vEndX, vEndY] = foundationPathCoordinates(vPath, planarGraphs);
    const pClass = vPriority === PathPriority.LEFT ? lClass : rClass;
    const pCondition = vPath.condition;
    const pIndex = vPath.halfEdgeIndex;
    const [nx, ny] = line2Normal(vStartX, vStartY, vEndX, vEndY);

    if (pIndex === oIndex || pIndex === oAdj) {
      continue;
    }

    const dot = vec2Dot(tx, ty, nx, ny);
    if (Math.abs(dot) < ANGLE_EPSILON) {
      continue;
    }

    let pOffset = sOffset;
    if (dot < 0) {
      pOffset = -pOffset;
    }

    if (vOffset !== undefined) {
      if (offsetProjection && vOffset < pOffset) {
        continue;
      } else if (!offsetProjection && vOffset > pOffset) {
        continue;
      }
    }

    vOffset = pOffset;
    vClass = pClass;
    vCondition = pCondition;
    vIndex = pIndex;
  }

  vOffset = vOffset ?? oOffset;

  return {
    vOffset,
    vClass,
    vCondition,
    vIndex,
  };
}

/**
 * @note @scotwilcox and @abrahamdrechsler I believe this is the place where we pass in the targetted slab settings to compute the datums for the foundation path from
 * the targetted settings when migrating them to targetted slab settings.
 * @jaggyluis to review and comment as well.
 */
export function getFoundationPathDatums(
  foundationPath: Pick<FoundationPath, "condition">,
  floorPath: Pick<FloorPath, "floorPriority" | "leftFloorAssembly" | "rightFloorAssembly">,
  level: Level,
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  slabSettings: SlabSettings,
  priority?: PathPriority,
): FoundationPathDatums {
  const floorPlateBottom = floorPathBottom(
    level,
    floorPath,
    framingComponents,
    slabComponents,
    sheathingComponents,
    priority,
  );
  const slabTop = floorPathTop(level, floorPath, priority);
  // slabBottom is calculated directly from slabTop (which includes instanceOffset via floorElevation)
  // This ensures all path.datums values consistently include instanceOffset for instanced levels
  const slabBottom = worldInchesNew(slabTop - slabDepthAtFoundationPath(foundationPath, slabSettings));

  return {
    floorPlateBottom,
    slabTop,
    slabBottom,
  };
}

export function foundationPathFloorPerimeterEnds({
  foundationPath,
  floorPath,
  ends,
  planarGraphs,
}: {
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  ends: FoundationPathEnds;
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  const { vCondition: startCondition, vOffset: start } = foundationPathFloorPerimeterEndCapOffset({
    foundationPath,
    floorPath,
    foundationPathsFromVertex: ends.fromStart.foundationPaths,
    floorPathsFromVertex: ends.fromStart.floorPaths,
    planarGraphs,
  });
  const oppositeFloorPath = floorPath ? floorPathOpposite(floorPath) : undefined;
  const { vCondition: endCondition, vOffset: end } = foundationPathFloorPerimeterEndCapOffset({
    foundationPath: foundationPathOpposite(foundationPath, planarGraphs),
    floorPath: oppositeFloorPath,
    foundationPathsFromVertex: ends.fromEnd.foundationPaths,
    floorPathsFromVertex: ends.fromEnd.floorPaths,
    planarGraphs,
  });
  return {
    start,
    startCondition,
    end,
    endCondition,
  };
}

export function foundationPathOffsetAlignment({
  foundationPath,
  floorPath,
  alignmentTarget = FoundationSectionAlignment.LEFT,
}: {
  foundationPath: Pick<FoundationPath, "subSystemOffset">;
  floorPath?: Pick<FloorPath, "floorPriority">;
  alignmentTarget?: FoundationSectionAlignment;
}): {
  offset: number;
  alignment: FoundationSectionAlignment;
} {
  const { floorPriority } = floorPath ?? { floorPriority: PathPriority.NONE };
  const { subSystemOffset } = foundationPath;

  let alignment = FoundationSectionAlignment.LEFT;
  let offset = subSystemOffset;

  // calculate offset & alignment
  if (floorPriority === PathPriority.NONE || alignmentTarget === FoundationSectionAlignment.CENTER) {
    alignment = FoundationSectionAlignment.CENTER;
    offset = 0;
  } else if (alignmentTarget === FoundationSectionAlignment.RIGHT) {
    alignment =
      floorPriority === PathPriority.LEFT ? FoundationSectionAlignment.RIGHT : FoundationSectionAlignment.LEFT;
  } else {
    alignment =
      floorPriority === PathPriority.LEFT ? FoundationSectionAlignment.LEFT : FoundationSectionAlignment.RIGHT;
  }

  return { offset, alignment };
}

export function isolatedFoundationPathOpenings({
  foundationPath,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  foundationPath: Pick<FoundationPath, "isolatedAssemblies" | "halfEdgeIndex" | "planarGraphId">;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): { openings: FoundationSectionOpeningDescriptor[] } {
  const openings: FoundationSectionOpeningDescriptor[] = [];

  if (isolatedFoundationSectionSizes.length > 0) {
    const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
    const { isolatedAssemblies } = foundationPath;

    if (vec2DistSquared(startX, startY, endX, endY) >= DIST_EPSILON_SQUARED) {
      const edgeAngle = Math.atan2(endY - startY, endX - startX);

      for (const assembly of isolatedAssemblies) {
        if (assembly.assemblyType === FoundationAssemblyType.VOID) {
          continue;
        }
        const {
          pierPositions: columnPositions = [],
          assemblyId,
          pierOffset: columnOffset,
          pierRotation = 0,
        } = assembly;

        const isolatedFoundationSectionSizesFiltered = isolatedFoundationSectionSizes.filter(
          (o) => o.assemblyId === assemblyId,
        );

        for (const isolatedFoundationSectionSize of isolatedFoundationSectionSizesFiltered) {
          const { top, depth, sectionComponent } = isolatedFoundationSectionSize;

          const sectionBoundary = isolatedFoundationSectionBoundary(sectionComponent, edgeAngle);

          if (!sectionBoundary) {
            continue;
          }

          let offsetX = 0,
            offsetY = 0;
          if (columnOffset) {
            [offsetX, offsetY] = vec2Rotate(columnOffset[0], columnOffset[1], edgeAngle);
          }
          for (const columnPosition of columnPositions) {
            const { param, paramNormalized, rotated } = columnPosition;
            const sectionBoundaryMod = polyLine2Copy(sectionBoundary);
            let totalPierRotation = pierRotation;
            if (rotated) {
              totalPierRotation += Math.PI / 2.0;
            }
            if (totalPierRotation !== 0.0) {
              polyLine2Rotate(sectionBoundaryMod, totalPierRotation, 0, 0);
            }
            const vec2d = line2Eval(startX, startY, endX, endY, param, paramNormalized);
            openings.push({
              outline: polyLine2Move(sectionBoundaryMod, vec2d[0] + offsetX, vec2d[1] + offsetY),
              top,
              depth,
              isIsolatedFoundation: true,
            });
          }
        }
      }
    }
  }
  return { openings };
}

export function foundationPathOpenings({
  foundationPath,
  floorPath,
  doorSettings,
  isolatedFoundationSectionSizes = [],
  gapOpeningAdjustment = 0,
  includeDoors = true,
  includeWindows = false,
  extendGarageDoorOpeningsToBOFoundation = false,
  planarGraphs,
}: {
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  doorSettings: DoorSettings;
  isolatedFoundationSectionSizes?: IsolatedFoundationSectionSize[];
  gapOpeningAdjustment?: number;
  includeDoors?: boolean;
  includeWindows?: boolean;
  extendGarageDoorOpeningsToBOFoundation?: boolean;
  planarGraphs: Table<PolygonPlanarGraph>;
}): { openings: FoundationSectionOpeningDescriptor[] } {
  const openings: FoundationSectionOpeningDescriptor[] = [];

  if (includeDoors && floorPath) {
    for (const door of floorPath.doors) {
      if (door.opening) {
        const { opening } = door;
        const planOutline = wallOpeningPlanExtents(opening);
        const frontOutline3 = wallOpeningOpening3(opening, "FRONT", false);
        const roughOpeningAdjustment = getDoorRoughOpeningAdjustment(door, doorSettings);
        const gapOffset = roughOpeningAdjustment + gapOpeningAdjustment;
        const outline = offsetPolyLine2(polyLine2Copy(planOutline), gapOffset);
        const aabb3 = polyLine3AABB3(ensureDefined(frontOutline3));
        const { minZ, maxZ } = aabb3;
        let depth = Math.abs(maxZ - minZ);
        const top = maxZ;
        if (isGarageDoorOperationType(door) && extendGarageDoorOpeningsToBOFoundation) {
          depth = Number.MAX_VALUE;
        }
        openings.push({
          outline,
          top,
          depth,
        });
      }
    }
  }

  if (includeWindows && floorPath) {
    for (const window of floorPath.windows) {
      if (window.opening) {
        const { opening } = window;
        const planOutline = wallOpeningPlanExtents(opening);
        const frontOutline3 = wallOpeningOpening3(opening, "FRONT", false);
        const roughOpeningAdjustment = 0.1; // TODO  - This value should be user exposed
        const gapOffset = roughOpeningAdjustment ?? 0 + gapOpeningAdjustment;
        const outline = offsetPolyLine2(polyLine2Copy(planOutline), gapOffset);
        const aabb3 = polyLine3AABB3(ensureDefined(frontOutline3));
        const { minZ, maxZ } = aabb3;
        const top = maxZ + gapOffset;
        const depth = Math.abs(maxZ - minZ) + gapOffset * 2;
        openings.push({
          outline,
          top,
          depth,
        });
      }
    }
  }

  if (isolatedFoundationSectionSizes.length) {
    const { openings: isolatedOpenings } = isolatedFoundationPathOpenings({
      foundationPath,
      isolatedFoundationSectionSizes,
      planarGraphs,
    });
    return { openings: [...openings, ...isolatedOpenings] };
  }

  return { openings };
}

export function isFootingSectionComponent(foundationSection: FoundationSection) {
  return foundationSection.size.sectionComponent.componentType === FoundationComponentType.FOOTING;
}

export function isPierSectionComponent(foundationSection: FoundationSection) {
  return (
    foundationSection.size.sizeType === FoundationSectionSizeType.ISOLATED &&
    foundationSection.size.sectionComponent.componentType === FoundationComponentType.PIER
  );
}

export function isStemWallSectionComponent(foundationSection: FoundationSection) {
  return (
    foundationSection.size.sizeType === FoundationSectionSizeType.LINEAR &&
    foundationSection.size.sectionComponent.componentType === FoundationComponentType.STEMWALL
  );
}

export function isCurbSectionComponent(foundationSection: FoundationSection) {
  return (
    foundationSection.size.sizeType === FoundationSectionSizeType.LINEAR &&
    foundationSection.size.sectionComponent.componentType === FoundationComponentType.CURB
  );
}

export function isInsetSectionType(foundationSection: FoundationSection) {
  return (
    foundationSection.size.sizeType === FoundationSectionSizeType.LINEAR &&
    foundationSection.size.sectionType === FoundationSectionType.INSET
  );
}

export function isLedgeSectionType(foundationSection: FoundationSection) {
  return (
    foundationSection.size.sizeType === FoundationSectionSizeType.LINEAR &&
    foundationSection.size.sectionType === FoundationSectionType.LEDGE
  );
}

export function isLinearFoundationSection(
  foundationSection: FoundationSection,
): foundationSection is LinearFoundationSection {
  return foundationSection.size.sizeType === FoundationSectionSizeType.LINEAR;
}

export function isIsolatedFoundationSection(
  foundationSection: FoundationSection,
): foundationSection is IsolatedFoundationSection {
  return foundationSection.size.sizeType === FoundationSectionSizeType.ISOLATED;
}

function intermediateExtrusionDescriptorNew(
  profile: PolyLine2,
  top: number,
  depth: number,
  includeInPlanProfiles: boolean,
  frameInfoOverrides?: Partial<IntermediateExtrusionFrameInfo>,
): IntermediateFoundationExtrusionDescriptor {
  const frameInfo: IntermediateExtrusionFrameInfo = {
    sourceDescriptorId: "generated",
    halfEdgeIndex: -1,
    sectionId: "generated",
    sectionIndex: -1,
    extrusionIndex: -1,
    sourceDirX: 0,
    sourceDirY: 0,
    sourceDirZ: -depth,
    sourcePlane: {
      originX: 0,
      originY: 0,
      originZ: top,
      uX: 1,
      uY: 0,
      uZ: 0,
      vX: 0,
      vY: 1,
      vZ: 0,
      ...frameInfoOverrides?.sourcePlane,
    },
    ...frameInfoOverrides,
  };
  return {
    profile: [profile],
    top,
    depth,
    includeInPlanProfiles,
    frameInfo,
  };
}

function isolatedFoundationSectionBoundary(sectionComponent: FoundationComponentEntity, rotAngle: number = 0) {
  let sectionBoundary: PolyLine2 | undefined;
  const { componentType } = sectionComponent;

  switch (componentType) {
    case FoundationComponentType.CURB:
    case FoundationComponentType.STEMWALL:
      break;
    case FoundationComponentType.FOOTING: {
      const footing = sectionComponent as FootingEntity;
      switch (footing.footingType) {
        case FootingType.CIRCULAR_ISOLATED: {
          const circularFooting = footing as CircularIsolatedFootingEntity;
          const { footingRadius } = circularFooting;
          sectionBoundary = arc2ByCenterStartAngle(0, 0, footingRadius, 0, Math.PI * 2, 0.001);
          sectionBoundary = polyLine2Close(sectionBoundary);
          break;
        }
        case FootingType.RECTANGULAR_ISOLATED: {
          const rectangularFooting = footing as RectangularIsolatedFootingEntity;
          const { footingWidth: width, footingLength: length } = rectangularFooting;
          sectionBoundary = polyLine2New([
            -0.5 * length,
            -0.5 * width,
            0.5 * length,
            -0.5 * width,
            0.5 * length,
            0.5 * width,
            -0.5 * length,
            0.5 * width,
            -0.5 * length,
            -0.5 * width,
          ]);
          polyLine2Rotate(sectionBoundary, rotAngle, 0, 0);
          break;
        }
        case FootingType.STRIP:
        case FootingType.TURNDOWN:
          break;
        default: {
          assertUnreachable(footing);
        }
      }
      break;
    }
    case FoundationComponentType.PIER: {
      const pier = sectionComponent as PierEntity;
      switch (pier.pierType) {
        case PierType.CIRCULAR_CMU:
        case PierType.CIRCULAR_CIP: {
          const circularPier = pier as CircularCMUPierEntity | CircularCIPPierEntity;
          const { pierRadius } = circularPier;
          sectionBoundary = arc2ByCenterStartAngle(0, 0, pierRadius, 0, Math.PI * 2, 0.001);
          sectionBoundary = polyLine2Close(sectionBoundary);
          break;
        }
        case PierType.RECTANGULAR_CMU:
        case PierType.RECTANGULAR_CIP: {
          const rectangularPier = pier as RectangularCMUPierEntity | RectangularCIPPierEntity;
          const { pierWidth: width, pierLength: length } = rectangularPier;
          sectionBoundary = polyLine2New([
            -0.5 * length,
            -0.5 * width,
            0.5 * length,
            -0.5 * width,
            0.5 * length,
            0.5 * width,
            -0.5 * length,
            0.5 * width,
            -0.5 * length,
            -0.5 * width,
          ]);
          polyLine2Rotate(sectionBoundary, rotAngle, 0, 0);
          break;
        }
        case PierType.CUSTOM:
          break;
        default: {
          assertUnreachable(pier);
        }
      }
      break;
    }
    default:
      assertUnreachable(componentType);
  }
  return sectionBoundary;
}

export function makeIsolatedFoundationSection({
  foundationPath,
  size,
  planarGraphs,
}: {
  foundationPath: FoundationPath;
  size: IsolatedFoundationSectionSize;
  planarGraphs: Table<PolygonPlanarGraph>;
}): IntermediateIsolatedFoundationSection {
  const [startX, startY, endX, endY] = foundationPathCoordinates(foundationPath, planarGraphs);
  const { isolatedAssemblies } = foundationPath;

  const planProfiles: PolyLine2[] = [];

  const { top, depth, sectionComponent, material } = size;

  if (vec2DistSquared(startX, startY, endX, endY) >= DIST_EPSILON_SQUARED) {
    const edgeAngle = Math.atan2(endY - startY, endX - startX);

    const sectionBoundary = isolatedFoundationSectionBoundary(sectionComponent, edgeAngle);

    if (sectionBoundary) {
      const filteredIsolatedAssemblies = isolatedAssemblies
        .filter(isSolidFoundationAssembly)
        .filter((sa) => sa.assemblyId === size.assemblyId);

      for (const assembly of filteredIsolatedAssemblies) {
        const { pierPositions: columnPositions = [], pierOffset: columnOffset, pierRotation = 0 } = assembly;

        let offsetX = 0,
          offsetY = 0;
        if (columnOffset) {
          [offsetX, offsetY] = vec2Rotate(columnOffset[0], columnOffset[1], edgeAngle);
        }

        for (const columnPosition of columnPositions) {
          const { param, paramNormalized, rotated } = columnPosition;
          const vec2d = line2Eval(startX, startY, endX, endY, param, paramNormalized);
          const sectionBoundaryMod = polyLine2Copy(sectionBoundary);
          let totalPierRotation = pierRotation;
          if (rotated) {
            totalPierRotation += Math.PI / 2.0;
          }
          if (totalPierRotation !== 0.0) {
            polyLine2Rotate(sectionBoundaryMod, totalPierRotation, 0, 0);
          }
          planProfiles.push(polyLine2Move(sectionBoundaryMod, vec2d[0] + offsetX, vec2d[1] + offsetY));
        }
      }
    }
  }
  const sectionId = uid();
  const extrusionDescriptors = planProfiles.map((planProfile, extrusionIndex) =>
    intermediateExtrusionDescriptorNew(planProfile, top, depth, true, {
      sourceDescriptorId: `${sectionId}:${extrusionIndex}`,
      halfEdgeIndex: foundationPath.halfEdgeIndex,
      sectionId,
      extrusionIndex,
    }),
  );

  return {
    id: sectionId,
    path: foundationPath,
    size,
    planProfiles,
    extrusionDescriptors,
    material: material ?? "foundation0",
  };
}

export function makeLinearFoundationSection({
  foundationPath,
  startCap,
  endCap,
  size,
}: {
  foundationPath: FoundationPath;
  startCap: WallEndCap;
  endCap: WallEndCap;
  size: LinearFoundationSectionSize;
}): IntermediateLinearFoundationSection {
  const { openings, material } = size;

  const start = startCap.verts;
  const end = endCap.verts;

  const rightGaps: WallGap[] = [];
  const rightEdge = polyLine2New([start[start.length - 2], start[start.length - 1], end[0], end[1]]);

  const leftGaps: WallGap[] = [];
  const leftEdge = polyLine2New([end[end.length - 2], end[end.length - 1], start[0], start[1]]);

  const wallPoly = polyLine2New([...rightEdge, ...leftEdge, rightEdge[0], rightEdge[1]]);
  const wallPolyOffSet = offsetPolyLine2(wallPoly, COMPACTNESS_EPSILON);

  //filter out isolated openings that are not part of the wall footprint (think offset isolated foundations)
  //ideally this should be other openings too which do not overlap? but that is causing too much grief in the regression tests
  const overlappingOpenings = openings.filter((opening) => {
    if (opening.isIsolatedFoundation) {
      const res = classifyContainmentPolyLine2PolyLine2(opening.outline, wallPolyOffSet);
      if (
        res === PolyLine2PolyLine2ContainmentClass.OUTSIDE ||
        res === PolyLine2PolyLine2ContainmentClass.OUTSIDE_COINCIDENT
      ) {
        return false;
      }
    }
    return true;
  });

  //only consider openings that are overlapping with the wall footprint
  for (const opening of overlappingOpenings) {
    const bottom = opening.top - opening.depth;
    const gapsLeft = makeWallGap(
      [],
      leftEdge,
      opening.outline,
      bottom,
      opening.depth,
      false,
      0,
      opening.isIsolatedFoundation,
    );
    const gapsRight = makeWallGap(
      [],
      rightEdge,
      opening.outline,
      bottom,
      opening.depth,
      false,
      0,
      opening.isIsolatedFoundation,
    );

    if (gapsLeft && gapsRight) {
      leftGaps.push(gapsLeft);
      rightGaps.push(gapsRight);
    }
  }

  let planProfiles: PolyLine2[] = [];
  let gapProfiles: PolyLine2[] = [];

  const sortedLeftGaps = startSortedWallGapsNew(leftEdge, leftGaps);
  const sortedRightGaps = startSortedWallGapsNew(rightEdge, rightGaps);

  if (leftGaps.length > 0) {
    planProfiles = makeCrossSectionFromWallGaps(sortedLeftGaps, sortedRightGaps);
    gapProfiles = makeWallGapCrossSections(sortedLeftGaps, sortedRightGaps);
  }

  if (!planProfiles.length) {
    planProfiles = [polyLine2New([...start, ...end, start[0], start[1]])];
  }

  const sectionId = uid();
  const extrusionDescriptors: IntermediateFoundationExtrusionDescriptor[] = planProfiles.map(
    (planProfile, extrusionIndex) =>
      intermediateExtrusionDescriptorNew(planProfile, size.top, size.depth, true, {
        sourceDescriptorId: `${sectionId}:${extrusionIndex}`,
        halfEdgeIndex: foundationPath.halfEdgeIndex,
        sectionId,
        extrusionIndex,
      }),
  );

  function fillGapBelow(gapProfile: PolyLine2, gapParams: WallGap) {
    let maxZ = gapParams.bottom;
    const minZ = size.top - size.depth;
    if (maxZ <= minZ) {
      return;
    }
    if (maxZ > size.top) {
      maxZ = size.top;
    }
    const depth = Math.abs(maxZ - minZ);
    extrusionDescriptors.push(
      intermediateExtrusionDescriptorNew(gapProfile, maxZ, depth, false, {
        sourceDescriptorId: `${sectionId}:gapBelow:${extrusionDescriptors.length}`,
        halfEdgeIndex: foundationPath.halfEdgeIndex,
        sectionId,
        extrusionIndex: extrusionDescriptors.length,
      }),
    );
  }

  function fillGapAbove(gapProfile: PolyLine2, gapParams: WallGap) {
    const maxZ = size.top;
    let minZ = gapParams.bottom + gapParams.height;
    if (maxZ <= minZ) {
      return;
    }
    if (minZ < size.top - size.depth) {
      minZ = size.top - size.depth;
    }
    const depth = Math.abs(maxZ - minZ);
    extrusionDescriptors.push(
      intermediateExtrusionDescriptorNew(gapProfile, maxZ, depth, false, {
        sourceDescriptorId: `${sectionId}:gapAbove:${extrusionDescriptors.length}`,
        halfEdgeIndex: foundationPath.halfEdgeIndex,
        sectionId,
        extrusionIndex: extrusionDescriptors.length,
      }),
    );
  }

  for (let i = 0; i < gapProfiles.length; i++) {
    // leftGaps has its order reversed with respect to gapProfiles's order.
    // This is why `rightGaps` is used instead.
    fillGapBelow(gapProfiles[i], sortedRightGaps.gaps[i]);
    fillGapAbove(gapProfiles[i], sortedRightGaps.gaps[i]);
  }

  return {
    id: sectionId,
    path: foundationPath,
    size,
    startCap,
    endCap,
    leftEdgeGaps: sortedLeftGaps,
    rightEdgeGaps: sortedRightGaps,
    planProfiles,
    gapProfiles,
    extrusionDescriptors,
    material: material ?? "foundation0",
  };
}

export function buildFoundationSectionExtrusions(section: IntermediateFoundationSection): FoundationSection {
  const extrusions = section.extrusionDescriptors.map((descriptor) => {
    let profilePlane = planeNewXY(0, 0, descriptor.top);
    let dirX = 0;
    let dirY = 0;
    let dirZ = -descriptor.depth;
    const frameInfo: IntermediateExtrusionFrameInfo = descriptor.frameInfo;
    const sourcePlane = frameInfo.sourcePlane;
    profilePlane = planeNew(
      sourcePlane.originX,
      sourcePlane.originY,
      descriptor.top,
      sourcePlane.uX,
      sourcePlane.uY,
      sourcePlane.uZ,
      sourcePlane.vX,
      sourcePlane.vY,
      sourcePlane.vZ,
    );
    if (Math.abs(frameInfo.sourceDirZ) > DIST_EPSILON) {
      const scale = descriptor.depth / Math.abs(frameInfo.sourceDirZ);
      dirX = frameInfo.sourceDirX * scale;
      dirY = frameInfo.sourceDirY * scale;
      dirZ = frameInfo.sourceDirZ * scale;
    } else {
      dirZ = frameInfo.sourceDirZ < 0 ? -descriptor.depth : descriptor.depth;
    }
    // Deferred foundation extrusion descriptors can carry loop winding from mixed sources
    // (overlap-modified and untouched). We normalize here because tessellation expects
    // outer loops to be CW and inner loops to be CCW.
    const normalizedLoops = descriptor.profile.map((loop) => polyLine2Copy(loop));
    if (normalizedLoops[0]) {
      polyLine2SetCW(normalizedLoops[0]);
      for (let i = 1; i < normalizedLoops.length; i++) {
        const holeLoop = normalizedLoops[i];
        if (!holeLoop) {
          continue;
        }
        polyLine2SetCCW(holeLoop);
      }
    }
    const face = faceNew(normalizedLoops, profilePlane);
    // Remove degenerate 0-length edges.
    faceSimplifyLoops(face);
    return extrudeFace(face, dirX, dirY, dirZ);
  });
  const planProfiles = section.extrusionDescriptors
    .filter((descriptor) => descriptor.includeInPlanProfiles)
    .map((descriptor) => descriptor.profile[0])
    .filter((profile): profile is PolyLine2 => Boolean(profile));

  if (section.size.sizeType === FoundationSectionSizeType.LINEAR) {
    const linearSection = section as IntermediateLinearFoundationSection;
    return {
      id: linearSection.id,
      path: linearSection.path,
      size: linearSection.size,
      startCap: linearSection.startCap,
      endCap: linearSection.endCap,
      leftEdgeGaps: linearSection.leftEdgeGaps,
      rightEdgeGaps: linearSection.rightEdgeGaps,
      planProfiles,
      gapProfiles: linearSection.gapProfiles,
      extrusions,
      material: linearSection.material,
    };
  }

  return {
    id: section.id,
    path: section.path,
    size: section.size,
    planProfiles,
    extrusions,
    material: section.material,
  };
}

export function foundationExtrusionSizeNew(
  top: number,
  depth: number,
  material?: ExteriorMaterial,
): FoundationExtrusionSize {
  return {
    top,
    depth,
    material,
  };
}

export function foundationSectionId(
  componentType: FoundationComponentType,
  sectionType: FoundationSectionType,
  sectionCode?: string,
  assemblyId?: string,
) {
  let baseId = `${componentType}_${sectionType}`;
  if (sectionCode !== undefined) {
    baseId += `_${sectionCode}`;
  }
  if (assemblyId !== undefined) {
    baseId += `_${assemblyId}`;
  }
  return baseId;
}

export function voidFoundationSectionSizeNew(sectionComponent, sectionId: string | undefined): FoundationSectionSize {
  return {
    sectionComponent,
    sectionId,
    sizeType: FoundationSectionSizeType.VOID,
  };
}

export function isIsolatedFoundationSectionSize(size: FoundationSectionSize): size is IsolatedFoundationSectionSize {
  return size.sizeType === FoundationSectionSizeType.ISOLATED;
}

function isolatedFoundationSectionSizeNew(
  sectionComponent: FoundationComponentEntity,
  sectionId: string,
  top: number,
  depth: number,
  left: number,
  right: number,
  assemblyId: string,
  material?: ExteriorMaterial,
  pickSource?: IsolatedFoundationPickSource,
): IsolatedFoundationSectionSize {
  return {
    sectionComponent,
    sectionId,
    sizeType: FoundationSectionSizeType.ISOLATED,
    sectionType: FoundationSectionType.COMPONENT,
    top,
    depth,
    left,
    right,
    assemblyId,
    material,
    pickSource,
  };
}

function linearFoundationSectionSizeNew(
  sectionComponent: FoundationComponentEntity,
  sectionType: FoundationSectionType,
  sectionId: string | undefined,
  top: number,
  depth: number,
  left: number,
  right: number,
  start: number = 0,
  end: number = 0,
  openings: FoundationSectionOpeningDescriptor[] = [],
  material?: ExteriorMaterial,
): LinearFoundationSectionSize {
  return {
    sectionId,
    sectionComponent,
    sectionType,
    sizeType: FoundationSectionSizeType.LINEAR,
    top,
    depth,
    left,
    right,
    start,
    end,
    openings,
    material,
  };
}

/**
 * @enum Utility enum for aligning foundation sections on a path
 * @member CENTER will align the section on the centerline a FoundationPath
 * @member LEFT will align the section right edge to the left side of a FoundationPath
 * @member RIGHT will align the section left edge to the right side of a FoundationPath
 */
export enum FoundationSectionAlignment {
  CENTER,
  LEFT,
  RIGHT,
}

export function isolatedFoundationSectionSizeFromAlignment({
  sectionComponent,
  sectionId,
  top,
  depth,
  width,
  alignment,
  assemblyId,
  offset = 0,
  material,
  pickSource,
}: {
  sectionComponent: FoundationComponentEntity;
  sectionType: FoundationSectionType;
  sectionId: string;
  alignment: FoundationSectionAlignment;
  top: number;
  depth: number;
  width: number;
  assemblyId: string;
  offset?: number; // offset this section from its alignment towards the RIGHT direction
  material?: ExteriorMaterial;
  pickSource?: IsolatedFoundationPickSource;
}): IsolatedFoundationSectionSize | FoundationSectionSize {
  let left: number;
  let right: number;

  if (width <= 0 || depth <= 0) {
    return voidFoundationSectionSizeNew(sectionComponent, sectionId);
  }

  switch (alignment) {
    case FoundationSectionAlignment.LEFT: {
      left = width;
      right = 0;
      break;
    }
    case FoundationSectionAlignment.RIGHT: {
      left = 0;
      right = width;
      break;
    }
    case FoundationSectionAlignment.CENTER:
    default: {
      const halfWidth = width / 2;
      left = halfWidth;
      right = halfWidth;
      break;
    }
  }

  left -= offset;
  right += offset;

  return isolatedFoundationSectionSizeNew(
    sectionComponent,
    sectionId,
    top,
    depth,
    left,
    right,
    assemblyId,
    material,
    pickSource,
  );
}

export function linearFoundationSectionSizeFromAlignment({
  sectionComponent,
  sectionType,
  sectionId,
  top,
  depth,
  width,
  alignment,
  start,
  end,
  offset = 0,
  openings,
  material,
}: {
  sectionComponent: FoundationComponentEntity;
  sectionType: FoundationSectionType;
  sectionId: string | undefined;
  alignment: FoundationSectionAlignment;
  top: number;
  depth: number;
  width: number;
  start?: number;
  end?: number;
  offset?: number; // offset this section from its alignment towards the RIGHT direction
  openings?: FoundationSectionOpeningDescriptor[];
  material?: ExteriorMaterial;
}): LinearFoundationSectionSize | FoundationSectionSize {
  let left: number;
  let right: number;

  if (width <= 0 || depth <= 0) {
    return voidFoundationSectionSizeNew(sectionComponent, sectionId);
  }

  switch (alignment) {
    case FoundationSectionAlignment.LEFT: {
      left = width;
      right = 0;
      break;
    }
    case FoundationSectionAlignment.RIGHT: {
      left = 0;
      right = width;
      break;
    }
    case FoundationSectionAlignment.CENTER:
    default: {
      const halfWidth = width / 2;
      left = halfWidth;
      right = halfWidth;
      break;
    }
  }

  left -= offset;
  right += offset;

  let filteredOpenings: FoundationSectionOpeningDescriptor[] | undefined;
  if (openings) {
    filteredOpenings = openings.filter((o) => foundationSectionOpeningOverlaps(o, top, top - depth));
  }

  return linearFoundationSectionSizeNew(
    sectionComponent,
    sectionType,
    sectionId,
    top,
    depth,
    left,
    right,
    start,
    end,
    filteredOpenings,
    material,
  );
}

function foundationSectionOpeningOverlaps(opening: FoundationSectionOpeningDescriptor, top: number, bottom: number) {
  const { top: oTop, depth: oDepth } = opening;
  const oBottom = oTop - oDepth;
  if (Math.abs(oBottom - top) < DIST_EPSILON) {
    return false;
  }
  if (Math.abs(oTop - bottom) < DIST_EPSILON) {
    return false;
  }
  return oBottom < top && oTop > bottom;
}

export function foundationSectionSizeOpposite(size: FoundationSectionSize): FoundationSectionSize {
  switch (size.sizeType) {
    case FoundationSectionSizeType.VOID: {
      return size;
    }
    case FoundationSectionSizeType.LINEAR: {
      return {
        ...size,
        left: size.right,
        right: size.left,
        start: size.end,
        end: size.start,
      };
    }
    case FoundationSectionSizeType.ISOLATED: {
      return {
        ...size,
        left: size.right,
        right: size.left,
      };
    }
    default: {
      assertUnreachable(size);
    }
  }
}

export function foundationSectionSizeToWallPathBase(halfEdgeIndex: number, size: FoundationSectionSize): WallPathBase {
  if (size.sizeType === FoundationSectionSizeType.VOID) {
    return {
      halfEdgeIndex,
      wallType: WallType.VOID,
    };
  } else {
    return {
      halfEdgeIndex,
      wallType: WallType.SOLID_MASS,
    };
  }
}

export function foundationSectionSizeToWallOffsetInfo(size: LinearFoundationSectionSize) {
  const { left, right } = size;
  return {
    leftOffset: left,
    rightOffset: right,
    wallThickness: right + left,
  };
}

export function foundationSectionSizeToWallButtCapExtension(size: LinearFoundationSectionSize): number {
  // this may become more complex
  return size.start;
}
